<template>
  <Doughnut
    :chart-options="chartOptions"
    :chart-data="chartData"
    :chart-id="chartId"
    :dataset-id-key="datasetIdKey"
    :plugins="[shadowGlowPlugin, centerTextPlugin, ...plugins]"
    :css-classes="cssClasses"
    :styles="styles"
    :width="width"
    :height="height"
  />
</template>

<script>
import { Doughnut } from 'vue-chartjs/legacy'
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  CategoryScale
} from 'chart.js'

ChartJS.register(Title, Tooltip, Legend, ArcElement, CategoryScale)

/** Soft shadow under arcs for depth */
const shadowGlowPlugin = {
  id: 'doughnutShadowGlow',
  beforeDraw(chart, _args, opts) {
    const { ctx } = chart
    ctx.save()
    ctx.shadowColor = opts?.shadowColor || 'rgba(0,0,0,0.18)'
    ctx.shadowBlur = opts?.shadowBlur || 14
    ctx.shadowOffsetX = 0
    ctx.shadowOffsetY = 8
  },
  afterDraw(chart) {
    chart.ctx.restore()
  }
}

/** Center text: big number (total or provided value) + small label */
const centerTextPlugin = {
  id: 'centerText',
  afterDraw(chart, _args, opts) {
    const { chartArea, ctx } = chart
    const dataset = chart.data.datasets?.[0]
    if (!dataset) return

    const total = (dataset.data || []).reduce((a, b) => a + Number(b || 0), 0)
    const bigText =
      (opts && (opts.value ?? opts.top) !== undefined)
        ? String(opts.value ?? opts.top)
        : String(total)
    const label = opts?.label ?? 'Totalt'

    const fontColor =
      getComputedStyle(document.documentElement).getPropertyValue('--ml-text-soft')?.trim() || '#6b7280'
    const textColor =
      getComputedStyle(document.documentElement).getPropertyValue('--ml-text')?.trim() || '#111827'

    const centerX = (chartArea.left + chartArea.right) / 2
    const centerY = (chartArea.top + chartArea.bottom) / 2

    ctx.save()
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'

    // Big number / custom text
    ctx.font = '600 20px Poppins, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial'
    ctx.fillStyle = textColor
    ctx.fillText(bigText, centerX, centerY - 8)

    // Subtitle
    ctx.font = '500 12px Poppins, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial'
    ctx.fillStyle = fontColor
    ctx.fillText(label, centerX, centerY + 12)
    ctx.restore()
  }
}

export default {
  name: 'MemlistDoughnutChart',
  components: { Doughnut },

  props: {
    // keep these general rendering props
    chartId: { type: String, default: 'doughnut-chart' },
    datasetIdKey: { type: String, default: 'label' },
    width: { type: Number, default: 400 },
    height: { type: Number, default: 400 },
    cssClasses: { type: String, default: '' },
    styles: { type: Object, default: () => ({}) },
    plugins: { type: Array, default: () => [] },

    // 🚩 the only data props you pass in:
    // 1) numbers
    values: {
      type: Array, // of numbers
      required: true,
      validator: a => Array.isArray(a) && a.every(v => !isNaN(Number(v)))
    },
    // 2) labels and colors
    labels: {
      type: Array, // of strings
      required: true,
      validator: a => Array.isArray(a) && a.every(v => typeof v === 'string')
    },
    /**
     * Colors can be:
     * - Array<string>  -> base hex/rgb per slice (we derive fill/border)
     * - { background: string[], border?: string[] }
     * If omitted or too short, we auto-fill from CSS-var palette.
     */
    colors: {
      type: [Array, Object],
      default: () => []
    },
    // 3) center content:
    // string => used as the small label (bottom), big number auto = total
    // object => { label?: string, value?: string|number } (value overrides total)
    center: {
      type: [String, Object],
      default: 'Totalt'
    },

    // optional: chart title text (kept internal but configurable)
    title: { type: String, default: '' }
  },

  data() {
    return {
      shadowGlowPlugin,
      centerTextPlugin
    }
  },

  computed: {
    // ensure all arrays have the same effective length
    _len() {
      return Math.min(this.values.length, this.labels.length)
    },

    _palette() {
      return this.getPalette()
    },

    _resolvedBackground() {
      // if user passed array => treat as base color list
      if (Array.isArray(this.colors) && this.colors.length) {
        return this.colors.slice(0, this._len).map(hex => this.hexToRgba(hex, 0.9))
      }
      // if user passed object with background
      if (!Array.isArray(this.colors) && this.colors?.background?.length) {
        return this.colors.background.slice(0, this._len)
      }
      // fallback to CSS-var palette
      return this._palette.fills.slice(0, this._len)
    },

    _resolvedBorder() {
      if (Array.isArray(this.colors) && this.colors.length) {
        return this.colors.slice(0, this._len).map(hex => this.hexToRgba(hex, 1))
      }
      if (!Array.isArray(this.colors) && this.colors?.border?.length) {
        return this.colors.border.slice(0, this._len)
      }
      return this._palette.borders.slice(0, this._len)
    },

    chartData() {
      const labels = this.labels.slice(0, this._len)
      const data = this.values.slice(0, this._len).map(n => Number(n) || 0)

      const total = data.reduce((a, b) => a + b, 0)

      // If no data, render one full segment in gray
      if (total === 0) {
        return {
          labels: ['No Data'],
          datasets: [
            {
              data: [1],
              backgroundColor: ['#d1d5db'], // Tailwind's gray-300
              borderColor: ['#d1d5db'],
              borderWidth: 2,
              hoverOffset: 0
            }
          ]
        }
      }

      return {
        labels,
        datasets: [
          {
            data,
            backgroundColor: this._resolvedBackground,
            borderColor: this._resolvedBorder,
            borderWidth: 2,
            hoverOffset: 10
          }
        ]
      }
    },

    chartOptions() {
      const centerCfg = typeof this.center === 'string'
        ? { label: this.center }
        : { label: this.center?.label ?? 'Totalt', value: this.center?.value }

      return {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '72%',
        rotation: -90, // start at top
        layout: { padding: 8 },
        plugins: {
          title: this.title
            ? { display: true, text: this.title, padding: { top: 4, bottom: 10 }, font: { size: 16, weight: '600' } }
            : { display: false },
          legend: null,
          
          tooltip: {
            padding: 10,
            displayColors: false,
            callbacks: {
              label: (ctx) => {
                const total = ctx.dataset.data.reduce((a, b) => a + Number(b || 0), 0) || 1
                const v = Number(ctx.parsed) || 0
                const pct = Math.round((v / total) * 100)
                return ` ${ctx.label}: ${v} (${pct}%)`
              }
            }
          },
          doughnutShadowGlow: { shadowBlur: 12, shadowColor: 'rgba(0,0,0,0.16)' },
          centerText: centerCfg
        }
      }
    }
  },

  methods: {
    getPalette() {
      // Pull from CSS vars when available; fall back to a tasteful set
      const vars = (name, fallback) =>
        getComputedStyle(document.documentElement).getPropertyValue(name)?.trim() || fallback
      const base = [
        vars('--ml-series-1', '#4f46e5'),
        vars('--ml-series-2', '#06b6d4'),
        vars('--ml-series-3', '#10b981'),
        vars('--ml-series-4', '#f59e0b'),
        vars('--ml-series-5', '#ef4444'),
        vars('--ml-series-6', '#a855f7')
      ]
      const fills = base.map(hex => this.hexToRgba(hex, 0.9))
      const borders = base.map(hex => this.hexToRgba(hex, 1))
      return { fills, borders }
    },
    hexToRgba(hex, a = 1) {
      const c = String(hex).replace('#', '')
      const full = c.length === 3 ? c.split('').map(ch => ch + ch).join('') : c
      // allow already-rgba strings
      if (/^rgba?\(/i.test(hex)) return hex
      const bigint = parseInt(full, 16)
      const r = (bigint >> 16) & 255
      const g = (bigint >> 8) & 255
      const b = bigint & 255
      return `rgba(${r}, ${g}, ${b}, ${a})`
    }
  }
}
</script>

<style scoped>
:deep(canvas) {
  border-radius: 14px;
}
</style>
